import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { ShieldCheck, AlertTriangle, FileText } from "lucide-react";
import { useI18n } from "../lib/i18n";

const LAST_UPDATED_EN = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
const LAST_UPDATED_TE = new Date().toLocaleDateString("te-IN", { day: "numeric", month: "long", year: "numeric" });

const COMPANY = "NeuroOpinion India";
const COMPANY_TE = "న్యూరోఒపీనియన్ ఇండియా";
const CITY = "Hyderabad";
const CITY_TE = "హైదరాబాద్";
const STATE = "Telangana";
const STATE_TE = "తెలంగాణ";
const COUNTRY = "India";
const COUNTRY_TE = "భారతదేశం";
const SUPPORT_EMAIL = "support@neuroopinion.in";
const GRIEVANCE_EMAIL = "grievance@neuroopinion.in";
const DPO_EMAIL = "dpo@neuroopinion.in";
const PRIVACY_EMAIL = "privacy@neuroopinion.in";

const H1 = ({ icon, title, color = "blue" }) => (
  <div className="flex items-center gap-3 mb-2" data-testid="legal-title">
    <div className={`w-11 h-11 rounded-xl bg-${color}-50 grid place-items-center text-${color}-700`}>{icon}</div>
    <h1 className="font-heading text-3xl font-semibold text-slate-900">{title}</h1>
  </div>
);

const H2 = ({ children, id }) => (
  <h2 id={id} className="font-heading text-xl font-semibold text-slate-900 mt-8 scroll-mt-24">{children}</h2>
);

function LangToggle() {
  const { lang, setLang } = useI18n();
  return (
    <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5 text-xs font-semibold" data-testid="legal-lang-toggle">
      <button
        onClick={() => setLang("en")}
        data-testid="legal-lang-en"
        className={`px-3 py-1 rounded-full transition ${lang === "en" ? "bg-blue-600 text-white" : "text-slate-600"}`}
      >
        English
      </button>
      <button
        onClick={() => setLang("te")}
        data-testid="legal-lang-te"
        className={`px-3 py-1 rounded-full transition ${lang === "te" ? "bg-blue-600 text-white" : "text-slate-600"}`}
      >
        తెలుగు
      </button>
    </div>
  );
}

const Layout = ({ children, testId }) => {
  const { lang } = useI18n();
  const labels = lang === "te"
    ? { back: "← హోమ్‌కు తిరిగి వెళ్ళండి", privacy: "గోప్యతా విధానం", terms: "సేవా నిబంధనలు", disclaimer: "వైద్య డిస్క్లెయిమర్" }
    : { back: "← Back to home", privacy: "Privacy Policy", terms: "Terms of Service", disclaimer: "Medical Disclaimer" };
  return (
    <div className="min-h-screen bg-slate-50" data-testid={testId}>
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Card className="p-8 border-slate-200">
          <div className="flex justify-end mb-4"><LangToggle /></div>
          {children}
          <div className="mt-10 flex flex-wrap gap-4 text-sm">
            <Link to="/" className="font-semibold text-blue-700" data-testid="legal-back-home">{labels.back}</Link>
            <Link to="/privacy" className="text-slate-600 hover:text-blue-700" data-testid="legal-link-privacy">{labels.privacy}</Link>
            <Link to="/terms" className="text-slate-600 hover:text-blue-700" data-testid="legal-link-terms">{labels.terms}</Link>
            <Link to="/disclaimer" className="text-slate-600 hover:text-blue-700" data-testid="legal-link-disclaimer">{labels.disclaimer}</Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

// ---------- PRIVACY POLICY ----------
function PrivacyEN() {
  return (
    <>
      <H1 icon={<ShieldCheck className="w-6 h-6" />} title="Privacy Policy" />
      <p className="text-sm text-slate-500 mb-6">Last updated: {LAST_UPDATED_EN}</p>
      <div className="prose prose-slate max-w-none text-sm text-slate-700 space-y-3 leading-relaxed">
        <p>This Privacy Policy describes how <b>{COMPANY}</b> (&quot;Company&quot;, &quot;we&quot;, &quot;our&quot;, &quot;us&quot;), operating from {CITY}, {STATE}, {COUNTRY}, collects, uses, stores, shares and protects the information of patients, doctors, caregivers and visitors (together, &quot;you&quot;, &quot;User&quot;) who access our website, mobile application, or any service (together, the &quot;Platform&quot;). By using the Platform you agree to this Policy, our <Link to="/terms" className="text-blue-700">Terms of Service</Link>, and our <Link to="/disclaimer" className="text-blue-700">Medical Disclaimer</Link>.</p>
        <p>This Policy is published in compliance with the Information Technology Act, 2000 and the rules thereunder (including the IT (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011), the Digital Personal Data Protection Act, 2023 (&quot;<b>DPDP Act</b>&quot;), the Telemedicine Practice Guidelines, 2020 issued under the Indian Medical Council Act, and applicable data-protection laws.</p>

        <H2 id="scope">1. Scope &amp; applicability</H2>
        <p>This Policy applies to all Users of the Platform globally, and specifically to individuals located in India whose personal data is processed by us as a Data Fiduciary under the DPDP Act. If you do not agree with this Policy, please do not use the Platform.</p>

        <H2 id="information">2. Information we collect</H2>
        <p>We collect the following categories of information, only to the extent necessary for the Platform to function:</p>
        <h3 className="font-semibold text-slate-900 mt-4">2.1 Information you provide</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Patient account:</b> full name, age, gender, mobile number, state of residence, preferred language.</li>
          <li><b>Medical &amp; health data</b> (Sensitive Personal Data under IT Rules &amp; DPDP Act): symptoms, medical history you share, medications, MRI/CT/X-ray/DICOM/PDF/JPG scan files, prior diagnosis, allergies, and any free-text you enter during booking or chat.</li>
          <li><b>Doctor account:</b> full name, email, MCI / NMC / State Medical Council registration number, qualifications, years of experience, specialization, clinic name, uploaded medical-licence document, KYC documents (where required for payouts), profile photo, consultation fee.</li>
          <li><b>Payment data:</b> the payment identifier, order id, invoice id, amount and status received from our payment processor (Cashfree). We <b>do not</b> store your full card number, CVV, UPI PIN or net-banking credentials.</li>
          <li><b>Communications:</b> messages in case chat, support emails, feedback.</li>
        </ul>
        <h3 className="font-semibold text-slate-900 mt-4">2.2 Information collected automatically</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Device type, operating system, browser, approximate IP-based location, crash logs.</li>
          <li>Usage analytics (pages viewed, features used, anonymised and sampled).</li>
          <li>Cookies and similar technologies (see Section 9).</li>
        </ul>
        <h3 className="font-semibold text-slate-900 mt-4">2.3 Information from third parties</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>OTP delivery status from SMS/WhatsApp providers (Fast2SMS, Meta WhatsApp Cloud API).</li>
          <li>Payment confirmation / failure events via Cashfree webhooks.</li>
          <li>Doctor registration validation against the public NMC / State Medical Council online registry.</li>
        </ul>

        <H2 id="purposes">3. Purposes &amp; legal basis</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Service delivery</b> — create your account, authenticate you via OTP, match you with a qualified specialist, let the assigned doctor review your scans, and return a written opinion.</li>
          <li><b>Payments &amp; invoicing</b> — charge the consultation fee, split platform / doctor share, issue GST invoices.</li>
          <li><b>Communication</b> — send transactional alerts via SMS, WhatsApp, in-app notification and email.</li>
          <li><b>Safety &amp; fraud prevention</b> — detect abuse, chargeback fraud, fake doctor profiles, spam.</li>
          <li><b>Legal compliance</b> — respond to lawful requests from Indian authorities, maintain records required under the Telemedicine Practice Guidelines 2020 and the Income-Tax Act.</li>
          <li><b>Product improvement</b> — analyse usage in aggregate. Medical records are <b>never</b> used for training AI models or shared with advertisers.</li>
        </ul>
        <p>The legal basis for processing is (a) your explicit <b>consent</b> given at signup and at the time of scan upload, (b) performance of the service you requested, and (c) compliance with law.</p>

        <H2 id="sharing">4. Who we share information with</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>The assigned doctor only</b> — sees your name, age, gender, symptoms and the scans you uploaded for the specific case they were matched to.</li>
          <li><b>Cashfree</b> (PCI-DSS Level 1) — for processing payments and refunds.</li>
          <li><b>Fast2SMS</b> and <b>Meta WhatsApp Cloud API</b> — for sending OTPs and notifications.</li>
          <li><b>Object-storage provider</b> — encrypted storage of scans and reports.</li>
          <li><b>Jitsi Meet</b> — for video consultations; we do not record calls.</li>
          <li><b>Law-enforcement agencies</b> — only pursuant to a valid Indian court order or statutory request.</li>
          <li><b>Business transfer</b> — successor entities will be bound by this Policy.</li>
        </ul>
        <p><b>We never sell, rent or license your data to advertisers, data brokers or unrelated third parties.</b></p>

        <H2 id="security">5. Data security</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>All traffic is served over TLS 1.2+ (HTTPS only).</li>
          <li>Passwords are hashed with bcrypt; OTPs expire after 10 minutes.</li>
          <li>Scans and reports are stored in encrypted object storage.</li>
          <li>Role-based access control; admins cannot read chat content.</li>
          <li>Audit logs of all administrative actions.</li>
          <li>We follow the <b>Reasonable Security Practices</b> prescribed under Rule 8 of the IT Rules, 2011.</li>
        </ul>
        <p>In the unlikely event of a personal-data breach we will notify affected users and the Data Protection Board of India within the timelines required by the DPDP Act.</p>

        <H2 id="retention">6. Data retention</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>Patient case records: <b>3 years</b> from opinion date (Telemedicine Practice Guidelines, 2020).</li>
          <li>Tax / payment records: <b>8 years</b> (Income-Tax Act and GST laws).</li>
          <li>Account profile data: while the account is active, deleted within 30 days of deletion request.</li>
          <li>Analytics and log data: up to 12 months.</li>
        </ul>

        <H2 id="rights">7. Your rights (DPDP Act, 2023)</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Access</b> — request a copy of the personal data we hold about you.</li>
          <li><b>Correction</b> — ask us to correct inaccurate or outdated information.</li>
          <li><b>Erasure</b> — ask us to delete your account and associated data.</li>
          <li><b>Withdraw consent</b> — at any time.</li>
          <li><b>Grievance redressal</b> — with our Grievance Officer.</li>
          <li><b>Nominate</b> — nominate another person to exercise your rights.</li>
        </ul>
        <p>To exercise any right, email <a href={`mailto:${PRIVACY_EMAIL}`} className="text-blue-700">{PRIVACY_EMAIL}</a> from the mobile number / email associated with your account.</p>

        <H2 id="children">8. Children&apos;s data</H2>
        <p>The Platform is not directed at children under 18. If a minor requires a second opinion, a parent or legal guardian must create the account and provide verifiable consent.</p>

        <H2 id="cookies">9. Cookies &amp; tracking</H2>
        <p>We use first-party cookies and localStorage for authentication, language preference, and anonymised product analytics. You can clear cookies from your browser at any time.</p>

        <H2 id="international">10. Cross-border transfers</H2>
        <p>Primary storage and processing happens within India. Some sub-processors may process data outside India in compliance with the DPDP Act and adequate safeguards.</p>

        <H2 id="grievance">11. Grievance Officer &amp; contact</H2>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="font-semibold text-slate-900">Grievance Officer</div>
          <div>Name: The Grievance Officer, {COMPANY}</div>
          <div>Address: {CITY}, {STATE}, {COUNTRY}</div>
          <div>Email: <a href={`mailto:${GRIEVANCE_EMAIL}`} className="text-blue-700">{GRIEVANCE_EMAIL}</a></div>
          <div className="mt-2 text-slate-600">Data Protection Officer: <a href={`mailto:${DPO_EMAIL}`} className="text-blue-700">{DPO_EMAIL}</a><br />Support: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700">{SUPPORT_EMAIL}</a></div>
        </div>
        <p>As per Rule 5(9) of the IT Rules, 2011 and Section 10 of the DPDP Act, the Grievance Officer shall acknowledge your complaint within 48 hours and resolve it within 30 days.</p>

        <H2 id="changes">12. Changes to this Policy</H2>
        <p>We may update this Policy from time to time. Material changes will be notified via email or in-app banner at least 14 days before they take effect.</p>

        <H2 id="consent">13. Consent</H2>
        <p>By creating an account, uploading a scan, or booking a consultation, you confirm that you have read this Policy and explicitly consent to the processing of your Sensitive Personal Data (including health data) for the purposes described above.</p>
      </div>
    </>
  );
}

function PrivacyTE() {
  return (
    <>
      <H1 icon={<ShieldCheck className="w-6 h-6" />} title="గోప్యతా విధానం" />
      <p className="text-sm text-slate-500 mb-6">చివరిగా నవీకరించబడింది: {LAST_UPDATED_TE}</p>
      <div className="prose prose-slate max-w-none text-sm text-slate-700 space-y-3 leading-relaxed">
        <p>ఈ గోప్యతా విధానం, {CITY_TE}, {STATE_TE}, {COUNTRY_TE} నుండి పనిచేస్తున్న <b>{COMPANY_TE}</b> (&quot;కంపెనీ&quot;, &quot;మేము&quot;, &quot;మా&quot;) రోగులు, వైద్యులు, సంరక్షకులు మరియు సందర్శకుల (&quot;మీరు&quot;, &quot;వినియోగదారు&quot;) సమాచారాన్ని ఎలా సేకరిస్తాం, ఉపయోగిస్తాం, నిల్వ చేస్తాం, పంచుకుంటాం మరియు రక్షిస్తామో వివరిస్తుంది. ప్లాట్‌ఫాం ఉపయోగించడం ద్వారా మీరు ఈ విధానానికి, మా <Link to="/terms" className="text-blue-700">సేవా నిబంధనలకు</Link> మరియు <Link to="/disclaimer" className="text-blue-700">వైద్య డిస్క్లెయిమర్‌కు</Link> అంగీకరిస్తున్నారు.</p>
        <p>ఈ విధానం ఇన్ఫర్మేషన్ టెక్నాలజీ యాక్ట్ 2000, దాని నియమాలు (IT Rules 2011), డిజిటల్ పర్సనల్ డేటా ప్రొటెక్షన్ యాక్ట్ 2023 (&quot;<b>DPDP చట్టం</b>&quot;), టెలీమెడిసిన్ ప్రాక్టీస్ మార్గదర్శకాలు 2020 మరియు వర్తించే డేటా-రక్షణ చట్టాలకు అనుగుణంగా ప్రచురించబడింది.</p>

        <H2>1. పరిధి &amp; వర్తింపు</H2>
        <p>ఈ విధానం ప్రపంచవ్యాప్తంగా అన్ని వినియోగదారులకు వర్తిస్తుంది, ప్రత్యేకంగా DPDP చట్టం కింద మేము డేటా ఫిడ్యూషియరీగా ప్రాసెస్ చేసే భారతదేశంలోని వ్యక్తులకు. మీరు ఈ విధానంతో అంగీకరించకపోతే, ప్లాట్‌ఫాంను ఉపయోగించవద్దు.</p>

        <H2>2. మేము సేకరించే సమాచారం</H2>
        <h3 className="font-semibold text-slate-900 mt-4">2.1 మీరు అందించే సమాచారం</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>రోగి ఖాతా:</b> పూర్తి పేరు, వయస్సు, లింగం, మొబైల్ నంబర్, నివాస రాష్ట్రం, ఇష్టమైన భాష.</li>
          <li><b>వైద్య &amp; ఆరోగ్య డేటా</b> (IT నియమాలు &amp; DPDP చట్టం ప్రకారం సున్నితమైన వ్యక్తిగత డేటా): లక్షణాలు, వైద్య చరిత్ర, మందులు, MRI/CT/X-రే/DICOM/PDF/JPG స్కాన్ ఫైళ్లు, ముందస్తు నిర్ధారణలు, అలెర్జీలు, చాట్‌లో మీరు నమోదు చేసే వివరాలు.</li>
          <li><b>వైద్యుడి ఖాతా:</b> పేరు, ఇమెయిల్, MCI / NMC / రాష్ట్ర వైద్య మండలి రిజిస్ట్రేషన్ నంబర్, అర్హతలు, అనుభవం, ప్రత్యేకత, క్లినిక్ పేరు, అప్‌లోడ్ చేసిన లైసెన్స్ డాక్యుమెంట్, KYC, ప్రొఫైల్ ఫోటో, కన్సల్టేషన్ రుసుము.</li>
          <li><b>చెల్లింపు డేటా:</b> Cashfree నుండి పేమెంట్ ఐడి, ఆర్డర్ ఐడి, మొత్తం మరియు స్థితి. మీ కార్డ్ నంబర్, CVV, UPI PIN లేదా నెట్ బ్యాంకింగ్ క్రెడెన్షియల్స్ మేము <b>నిల్వ చేయం</b>.</li>
          <li><b>కమ్యూనికేషన్లు:</b> కేస్ చాట్‌లోని సందేశాలు, మద్దతు ఇమెయిళ్లు, అభిప్రాయం.</li>
        </ul>
        <h3 className="font-semibold text-slate-900 mt-4">2.2 స్వయంచాలకంగా సేకరించేది</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>పరికరం, OS, బ్రౌజర్, IP ఆధారిత సుమారు స్థానం, క్రాష్ లాగ్‌లు.</li>
          <li>వినియోగ విశ్లేషణలు (పేజీ వీక్షణలు, అజ్ఞాతంగా మరియు నమూనాగా).</li>
          <li>కుకీలు మరియు సారూప్య సాంకేతికతలు (సెక్షన్ 9 చూడండి).</li>
        </ul>
        <h3 className="font-semibold text-slate-900 mt-4">2.3 మూడవ పక్షాల నుండి</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>OTP డెలివరీ స్థితి (Fast2SMS, Meta WhatsApp Cloud API).</li>
          <li>Cashfree వెబ్‌హుక్‌ల ద్వారా చెల్లింపు నిర్ధారణ / వైఫల్య సంఘటనలు.</li>
          <li>పబ్లిక్ NMC / రాష్ట్ర మండలి రిజిస్ట్రీ ద్వారా వైద్యుడి రిజిస్ట్రేషన్ ధృవీకరణ.</li>
        </ul>

        <H2>3. ప్రయోజనాలు &amp; చట్టపరమైన ఆధారం</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>సేవా పంపిణీ</b> — ఖాతా సృష్టించడం, OTP ద్వారా ప్రమాణీకరణ, నిపుణుడితో మ్యాచింగ్, స్కాన్ సమీక్ష, వ్రాతపూర్వక అభిప్రాయం అందించడం.</li>
          <li><b>చెల్లింపులు &amp; ఇన్‌వాయిసింగ్</b> — కన్సల్టేషన్ రుసుము వసూలు, ప్లాట్‌ఫాం / వైద్యుడి వాటా విభజన, GST ఇన్‌వాయిస్‌లు.</li>
          <li><b>కమ్యూనికేషన్</b> — SMS, WhatsApp, ఇన్-యాప్ నోటిఫికేషన్, ఇమెయిల్ ద్వారా లావాదేవీ అలర్ట్‌లు.</li>
          <li><b>భద్రత &amp; మోసం నివారణ</b> — దుర్వినియోగం, చార్జ్‌బ్యాక్ మోసం, నకిలీ వైద్యుల ఖాతాలు, స్పామ్‌లను గుర్తించడం.</li>
          <li><b>చట్టపరమైన అనుగుణ్యత</b> — భారత అధికారుల చట్టబద్ధ అభ్యర్థనలకు ప్రతిస్పందన.</li>
          <li><b>ఉత్పత్తి మెరుగుదల</b> — సమష్టిగా వినియోగాన్ని విశ్లేషించడం. వైద్య రికార్డులు AI శిక్షణకు లేదా ప్రకటనకర్తలతో <b>ఎప్పుడూ</b> పంచుకోబడవు.</li>
        </ul>
        <p>ప్రాసెస్ చేయడానికి చట్టపరమైన ఆధారం (ఎ) సైనప్‌లో మరియు స్కాన్ అప్‌లోడ్ సమయంలో మీ స్పష్టమైన <b>అంగీకారం</b>, (బి) మీరు అభ్యర్థించిన సేవ యొక్క ప్రదర్శన, (సి) చట్టానికి అనుగుణ్యత.</p>

        <H2>4. మేము ఎవరితో డేటా పంచుకుంటాం</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>అప్పగించిన వైద్యుడు మాత్రమే</b> — మీ పేరు, వయస్సు, లింగం, లక్షణాలు, స్కాన్‌లు చూస్తాడు.</li>
          <li><b>Cashfree</b> (PCI-DSS లెవల్ 1) — చెల్లింపులు మరియు రీఫండ్‌ల కోసం.</li>
          <li><b>Fast2SMS</b> మరియు <b>Meta WhatsApp Cloud API</b> — OTP మరియు నోటిఫికేషన్‌ల కోసం.</li>
          <li><b>ఆబ్జెక్ట్ స్టోరేజ్ ప్రొవైడర్</b> — స్కాన్‌లు మరియు నివేదికల ఎన్‌క్రిప్టెడ్ నిల్వ.</li>
          <li><b>Jitsi Meet</b> — వీడియో కన్సల్టేషన్‌ల కోసం; కాల్‌లను మేము రికార్డ్ చేయం.</li>
          <li><b>చట్ట అమలు ఏజెన్సీలు</b> — చెల్లుబాటు అయ్యే భారత కోర్టు ఉత్తర్వు లేదా చట్టబద్ధ అభ్యర్థన ప్రకారం మాత్రమే.</li>
          <li><b>వ్యాపార బదిలీ</b> — వారసత్వ సంస్థలు ఈ విధానానికి కట్టుబడి ఉంటాయి.</li>
        </ul>
        <p><b>మీ డేటాను ప్రకటనకర్తలకు, డేటా బ్రోకర్లకు లేదా సంబంధం లేని మూడవ పక్షాలకు మేము ఎప్పుడూ విక్రయించం, అద్దెకు ఇవ్వం లేదా లైసెన్స్ చేయం.</b></p>

        <H2>5. డేటా భద్రత</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>అన్ని ట్రాఫిక్ TLS 1.2+ (HTTPS మాత్రమే) ద్వారా.</li>
          <li>పాస్‌వర్డ్‌లు bcrypt ద్వారా హాష్ చేయబడతాయి; OTP‌లు 10 నిమిషాల తర్వాత గడువు ముగుస్తుంది.</li>
          <li>స్కాన్‌లు మరియు నివేదికలు ఎన్‌క్రిప్టెడ్ ఆబ్జెక్ట్ స్టోరేజ్‌లో నిల్వ చేయబడతాయి.</li>
          <li>రోల్-ఆధారిత యాక్సెస్ నియంత్రణ; నిర్వాహకులు చాట్ కంటెంట్‌ను చదవలేరు.</li>
          <li>అన్ని పరిపాలనా చర్యల ఆడిట్ లాగ్‌లు.</li>
          <li>IT Rules 2011 నియమం 8 ప్రకారం <b>సహేతుకమైన భద్రతా పద్ధతులను</b> అనుసరిస్తాం.</li>
        </ul>
        <p>వ్యక్తిగత డేటా ఉల్లంఘన జరిగిన అరుదైన సందర్భంలో, DPDP చట్టం అవసరమైన గడువులోపు ప్రభావితమైన వినియోగదారులకు మరియు భారత డేటా ప్రొటెక్షన్ బోర్డ్‌కు తెలియజేస్తాం.</p>

        <H2>6. డేటా నిలుపుదల</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>రోగి కేస్ రికార్డులు: అభిప్రాయం తేదీ నుండి <b>3 సంవత్సరాలు</b> (టెలీమెడిసిన్ ప్రాక్టీస్ మార్గదర్శకాలు 2020).</li>
          <li>పన్ను / చెల్లింపు రికార్డులు: <b>8 సంవత్సరాలు</b> (ఆదాయపు పన్ను చట్టం మరియు GST చట్టాలు).</li>
          <li>ఖాతా ప్రొఫైల్ డేటా: ఖాతా యాక్టివ్‌గా ఉన్నంతవరకు, తొలగింపు అభ్యర్థన తర్వాత 30 రోజుల్లో తొలగించబడుతుంది.</li>
          <li>విశ్లేషణలు మరియు లాగ్ డేటా: 12 నెలల వరకు.</li>
        </ul>

        <H2>7. మీ హక్కులు (DPDP చట్టం 2023)</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>యాక్సెస్</b> — మీ వ్యక్తిగత డేటా కాపీని అభ్యర్థించండి.</li>
          <li><b>సవరణ</b> — తప్పు లేదా పాత సమాచారాన్ని సరిదిద్దమని అడగండి.</li>
          <li><b>తొలగింపు</b> — మీ ఖాతా మరియు సంబంధిత డేటాను తొలగించమని అడగండి.</li>
          <li><b>అంగీకారం ఉపసంహరణ</b> — ఎప్పుడైనా.</li>
          <li><b>ఫిర్యాదు పరిష్కారం</b> — మా ఫిర్యాదు అధికారితో.</li>
          <li><b>నామినేషన్</b> — మీ హక్కులను వినియోగించడానికి మరొక వ్యక్తిని నామినేట్ చేయండి.</li>
        </ul>
        <p>ఏదైనా హక్కును ఉపయోగించుకోవడానికి, మీ ఖాతాతో అనుబంధించిన మొబైల్ / ఇమెయిల్ నుండి <a href={`mailto:${PRIVACY_EMAIL}`} className="text-blue-700">{PRIVACY_EMAIL}</a>కు ఇమెయిల్ చేయండి.</p>

        <H2>8. పిల్లల డేటా</H2>
        <p>ఈ ప్లాట్‌ఫాం 18 ఏళ్లలోపు పిల్లలకు ఉద్దేశించబడలేదు. మైనర్‌కు సెకండ్ ఒపీనియన్ అవసరమైతే, తల్లిదండ్రులు లేదా చట్టపరమైన సంరక్షకుడు ఖాతా సృష్టించి ధృవీకరించదగిన అంగీకారం ఇవ్వాలి.</p>

        <H2>9. కుకీలు &amp; ట్రాకింగ్</H2>
        <p>మేము ప్రమాణీకరణ, భాషా ప్రాధాన్యత మరియు అజ్ఞాత ఉత్పత్తి విశ్లేషణల కోసం ఫస్ట్-పార్టీ కుకీలు మరియు localStorageను ఉపయోగిస్తాం. మీరు ఎప్పుడైనా బ్రౌజర్ నుండి కుకీలను తొలగించవచ్చు.</p>

        <H2>10. సరిహద్దు దాటిన బదిలీలు</H2>
        <p>ప్రాథమిక నిల్వ మరియు ప్రాసెసింగ్ భారతదేశంలోనే జరుగుతుంది. కొన్ని సబ్-ప్రాసెసర్లు DPDP చట్టానికి అనుగుణంగా భారతదేశం వెలుపల డేటాను ప్రాసెస్ చేయవచ్చు.</p>

        <H2>11. ఫిర్యాదు అధికారి &amp; సంప్రదింపు</H2>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="font-semibold text-slate-900">ఫిర్యాదు అధికారి</div>
          <div>పేరు: ఫిర్యాదు అధికారి, {COMPANY_TE}</div>
          <div>చిరునామా: {CITY_TE}, {STATE_TE}, {COUNTRY_TE}</div>
          <div>ఇమెయిల్: <a href={`mailto:${GRIEVANCE_EMAIL}`} className="text-blue-700">{GRIEVANCE_EMAIL}</a></div>
          <div className="mt-2 text-slate-600">డేటా ప్రొటెక్షన్ ఆఫీసర్: <a href={`mailto:${DPO_EMAIL}`} className="text-blue-700">{DPO_EMAIL}</a><br />మద్దతు: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700">{SUPPORT_EMAIL}</a></div>
        </div>
        <p>IT Rules 2011 నియమం 5(9) మరియు DPDP చట్టం సెక్షన్ 10 ప్రకారం, ఫిర్యాదు అధికారి మీ ఫిర్యాదును 48 గంటల్లో అంగీకరిస్తారు మరియు 30 రోజుల్లో పరిష్కరిస్తారు.</p>

        <H2>12. ఈ విధానంలో మార్పులు</H2>
        <p>ఈ విధానాన్ని ఎప్పటికప్పుడు నవీకరించవచ్చు. ముఖ్యమైన మార్పులను అమలులోకి రావడానికి కనీసం 14 రోజుల ముందు ఇమెయిల్ లేదా ఇన్-యాప్ బ్యానర్ ద్వారా తెలియజేస్తాం.</p>

        <H2>13. అంగీకారం</H2>
        <p>ఖాతా సృష్టించడం, స్కాన్ అప్‌లోడ్ చేయడం లేదా కన్సల్టేషన్ బుక్ చేయడం ద్వారా, మీరు ఈ విధానాన్ని చదివారని, పైన వివరించిన ప్రయోజనాల కోసం మీ సున్నితమైన వ్యక్తిగత డేటా (ఆరోగ్య డేటాతో సహా) ప్రాసెసింగ్‌కు స్పష్టంగా అంగీకరిస్తున్నారని నిర్ధారిస్తారు.</p>
      </div>
    </>
  );
}

export function PrivacyPolicy() {
  const { lang } = useI18n();
  return <Layout testId="privacy-page">{lang === "te" ? <PrivacyTE /> : <PrivacyEN />}</Layout>;
}

// ---------- TERMS OF SERVICE ----------
function TermsEN() {
  return (
    <>
      <H1 icon={<FileText className="w-6 h-6" />} title="Terms of Service" color="indigo" />
      <p className="text-sm text-slate-500 mb-6">Last updated: {LAST_UPDATED_EN}</p>
      <div className="prose prose-slate max-w-none text-sm text-slate-700 space-y-3 leading-relaxed">
        <p>These Terms of Service (&quot;Terms&quot;) are a legally binding agreement between you and <b>{COMPANY}</b>, operating from {CITY}, {STATE}, {COUNTRY}, governing your access to and use of our website, mobile application and services (the &quot;Platform&quot;). By registering, booking a consultation, or otherwise using the Platform, you agree to these Terms, our <Link to="/privacy" className="text-blue-700">Privacy Policy</Link>, and our <Link to="/disclaimer" className="text-blue-700">Medical Disclaimer</Link>. If you do not agree, do not use the Platform.</p>
        <p>This electronic record is generated by a computer system and does not require any physical or digital signature. It is published in accordance with Rule 3(1) of the IT (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021.</p>

        <H2>1. Nature of the service</H2>
        <p>The Platform offers <b>asynchronous neurology / neurosurgery second-opinion consultations</b> delivered by independent medical practitioners registered with the Medical Council of India / National Medical Commission (&quot;Doctors&quot;). The Platform is an <b>intermediary</b> within the meaning of Section 2(1)(w) of the Information Technology Act, 2000 — it merely provides technology infrastructure to connect Patients and Doctors.</p>
        <p>All teleconsultations comply with the <b>Telemedicine Practice Guidelines, 2020</b> read with the Indian Medical Council (Professional Conduct, Etiquette and Ethics) Regulations, 2002.</p>

        <H2>2. Eligibility</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>You are at least 18 years old, or the verified parent / legal guardian of the Patient.</li>
          <li>You have the legal capacity to enter into a binding contract under the Indian Contract Act, 1872.</li>
          <li>You are not barred from receiving services under any applicable law.</li>
          <li>Doctors additionally warrant a valid and current MCI / NMC / State Medical Council registration.</li>
        </ul>

        <H2>3. Account &amp; OTP authentication</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>Patients authenticate via mobile OTP (SMS / WhatsApp). Doctors authenticate via email and password.</li>
          <li>You are solely responsible for keeping your OTP and password confidential. Any activity under your account is deemed to be by you.</li>
          <li>Notify us at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700">{SUPPORT_EMAIL}</a> if you suspect unauthorised access.</li>
        </ul>

        <H2>4. Booking, fees &amp; payments</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>Written second opinion: <b>₹1,000</b>. Video consultation: <b>₹2,000</b>. Fees may change in future and will always be displayed before you pay.</li>
          <li>Payments are processed by <b>Cashfree</b>. We do not store your full card number, CVV or UPI PIN.</li>
          <li>A tax invoice (incl. GST where applicable) is generated on successful payment.</li>
          <li>Platform commission (up to 20%) is retained by {COMPANY}; the balance is paid to the Doctor.</li>
        </ul>

        <H2>5. Cancellation &amp; refund policy</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Before doctor assignment</b> — full refund on request.</li>
          <li><b>After assignment but before opinion delivered</b> — full refund if review not started, otherwise 50% partial refund.</li>
          <li><b>After opinion is delivered</b> — no refund.</li>
          <li><b>Doctor declines / no response within 48h (Standard) / 6h (Emergency)</b> — automatic full refund.</li>
          <li>Refunds are credited to the original payment source within 5–7 working days.</li>
        </ul>

        <H2>6. Doctor–patient relationship &amp; scope of opinion</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>A limited teleconsultation relationship is formed between the assigned Doctor and the Patient for the single case booked.</li>
          <li>The opinion is based solely on the information and scans you provide.</li>
          <li>The opinion is <b>not a substitute</b> for an in-person examination.</li>
          <li>The Platform does <b>not</b> prescribe Schedule X drugs or narcotics.</li>
        </ul>

        <H2>7. Acceptable use</H2>
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Upload false, fabricated, or another person&apos;s medical records without authorisation.</li>
          <li>Harass, abuse, defame, threaten or impersonate anyone.</li>
          <li>Attempt to reverse-engineer, scrape, or interfere with the security of the Platform.</li>
          <li>Use the Platform for any unlawful, fraudulent, or harmful purpose.</li>
          <li>Record / share Jitsi video consultations or doctor opinions without the Doctor&apos;s written consent.</li>
        </ul>

        <H2>8. Doctor obligations</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>Maintain a valid MCI / NMC registration throughout.</li>
          <li>Respond to assigned cases within the applicable SLA (48h / 6h).</li>
          <li>Maintain patient confidentiality per the MCI Code of Ethics and DPDP Act.</li>
          <li>Not solicit patients outside the Platform for at least 12 months after an opinion is delivered.</li>
          <li>Issue reports only in their own name and registration number; ghost-writing is strictly prohibited.</li>
        </ul>

        <H2>9. Intellectual property</H2>
        <p>All content on the Platform is owned by or licensed to {COMPANY} and protected under Indian and international copyright and trademark law. You retain ownership of the medical records you upload and grant us a limited, non-exclusive, royalty-free licence to store, display and transmit them solely to deliver the service.</p>

        <H2>10. Disclaimers &amp; limitation of liability</H2>
        <p>The Platform is provided on an &quot;as is&quot; and &quot;as available&quot; basis. {COMPANY} shall not be liable for any indirect, incidental, special, consequential or punitive damages, medical outcomes arising out of the opinion, or losses caused by third-party service delays. Our aggregate liability shall not exceed the fee paid in the preceding 90 days, subject to a maximum of <b>₹10,000</b>.</p>

        <H2>11. Indemnity</H2>
        <p>You agree to indemnify and hold harmless {COMPANY}, its Doctors, officers, directors, employees and agents from any claim, liability, loss, damage, cost or expense arising out of your violation of these Terms or applicable law.</p>

        <H2>12. Termination &amp; suspension</H2>
        <p>We may suspend or terminate your account at any time if we reasonably believe you have breached these Terms. You may terminate your account at any time by emailing <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700">{SUPPORT_EMAIL}</a>.</p>

        <H2>13. Governing law &amp; jurisdiction</H2>
        <p>These Terms are governed by the laws of India. All disputes shall be subject to the <b>exclusive jurisdiction of the courts at {CITY}, {STATE}</b>. Parties agree to attempt good-faith mediation under the Mediation Act, 2023 within 60 days prior to litigation.</p>

        <H2>14. Grievance Officer</H2>
        <p>As required under Rule 3(2) of the IT (Intermediary Guidelines) Rules, 2021 and Section 10 of the DPDP Act, 2023, grievances may be addressed to:<br /><b>Grievance Officer, {COMPANY}</b>, {CITY}, {STATE}, {COUNTRY}<br />Email: <a href={`mailto:${GRIEVANCE_EMAIL}`} className="text-blue-700">{GRIEVANCE_EMAIL}</a><br />Response time: 48 hours (ack), 30 days (resolution).</p>

        <H2>15. Modifications</H2>
        <p>We may modify these Terms at any time. Material changes will be communicated at least 14 days prior to taking effect. Continued use constitutes acceptance.</p>

        <H2>16. Miscellaneous</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Severability:</b> If any provision is unenforceable, the remaining provisions remain in full force.</li>
          <li><b>Waiver:</b> Failure to enforce any right is not a waiver of that right.</li>
          <li><b>Assignment:</b> You may not assign these Terms; we may assign to a successor.</li>
          <li><b>Entire agreement:</b> These Terms, the Privacy Policy and the Medical Disclaimer constitute the entire agreement.</li>
          <li><b>Force majeure:</b> Neither party is liable for delays caused by events beyond reasonable control.</li>
        </ul>
      </div>
    </>
  );
}

function TermsTE() {
  return (
    <>
      <H1 icon={<FileText className="w-6 h-6" />} title="సేవా నిబంధనలు" color="indigo" />
      <p className="text-sm text-slate-500 mb-6">చివరిగా నవీకరించబడింది: {LAST_UPDATED_TE}</p>
      <div className="prose prose-slate max-w-none text-sm text-slate-700 space-y-3 leading-relaxed">
        <p>ఈ సేవా నిబంధనలు (&quot;నిబంధనలు&quot;), {CITY_TE}, {STATE_TE}, {COUNTRY_TE} నుండి పనిచేస్తున్న <b>{COMPANY_TE}</b>కు మరియు మీకు మధ్య చట్టబద్ధంగా కట్టుబడి ఉండే ఒప్పందం. ప్లాట్‌ఫాం యాక్సెస్ చేయడం లేదా ఉపయోగించడం ద్వారా, మీరు ఈ నిబంధనలకు, మా <Link to="/privacy" className="text-blue-700">గోప్యతా విధానానికి</Link> మరియు <Link to="/disclaimer" className="text-blue-700">వైద్య డిస్క్లెయిమర్‌కు</Link> అంగీకరిస్తున్నారు. మీరు అంగీకరించకపోతే, ప్లాట్‌ఫాంను ఉపయోగించవద్దు.</p>
        <p>ఈ ఎలక్ట్రానిక్ రికార్డ్ కంప్యూటర్ సిస్టమ్ ద్వారా రూపొందించబడింది, భౌతిక లేదా డిజిటల్ సంతకం అవసరం లేదు. IT (ఇంటర్మీడియరీ మార్గదర్శకాలు) నియమాలు 2021 నియమం 3(1) ప్రకారం ప్రచురించబడింది.</p>

        <H2>1. సేవ యొక్క స్వభావం</H2>
        <p>ఈ ప్లాట్‌ఫాం MCI / NMC రిజిస్టర్డ్ స్వతంత్ర వైద్యుల ద్వారా <b>అసింక్రోనస్ న్యూరాలజీ / న్యూరోసర్జరీ సెకండ్ ఒపీనియన్ కన్సల్టేషన్‌లను</b> అందిస్తుంది. ప్లాట్‌ఫాం IT చట్టం 2000 సెక్షన్ 2(1)(w) ప్రకారం <b>ఇంటర్మీడియరీ</b> — రోగులు మరియు వైద్యులను కలపడానికి సాంకేతిక మౌలిక సదుపాయాలను మాత్రమే అందిస్తుంది.</p>
        <p>అన్ని టెలీ-కన్సల్టేషన్‌లు <b>టెలీమెడిసిన్ ప్రాక్టీస్ మార్గదర్శకాలు 2020</b> మరియు MCI (ప్రొఫెషనల్ కండక్ట్, ఎటికెట్ మరియు ఎథిక్స్) నిబంధనలు 2002కు అనుగుణంగా ఉంటాయి.</p>

        <H2>2. అర్హత</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>మీరు కనీసం 18 ఏళ్లు, లేదా రోగి యొక్క ధృవీకరించబడిన తల్లిదండ్రులు / చట్టపరమైన సంరక్షకుడు.</li>
          <li>భారత కాంట్రాక్ట్ చట్టం 1872 కింద చట్టపరమైన సామర్థ్యం కలిగి ఉన్నారు.</li>
          <li>ఏ చట్టం కింద సేవలు పొందడానికి నిషేధించబడలేదు.</li>
          <li>వైద్యులు అదనంగా చెల్లుబాటు అయ్యే MCI / NMC / రాష్ట్ర మండలి రిజిస్ట్రేషన్‌ను హామీ ఇస్తారు.</li>
        </ul>

        <H2>3. ఖాతా &amp; OTP ప్రమాణీకరణ</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>రోగులు మొబైల్ OTP (SMS / WhatsApp) ద్వారా, వైద్యులు ఇమెయిల్ మరియు పాస్‌వర్డ్ ద్వారా ప్రమాణీకరిస్తారు.</li>
          <li>మీ OTP మరియు పాస్‌వర్డ్ గోప్యంగా ఉంచడం మీ బాధ్యత. మీ ఖాతా కింద జరిగే ఏ చర్య అయినా మీదిగా పరిగణించబడుతుంది.</li>
          <li>అనధికార యాక్సెస్ అనుమానం ఉంటే <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700">{SUPPORT_EMAIL}</a>కు తెలియజేయండి.</li>
        </ul>

        <H2>4. బుకింగ్, రుసుములు &amp; చెల్లింపులు</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>ప్రామాణిక సెకండ్ ఒపీనియన్: <b>₹1,000</b>. ఎమర్జెన్సీ / ప్రాధాన్యత: <b>₹2,000</b>. అంతర్జాతీయ రోగులకు: ఆఫ్రికా <b>$50 USD</b> / <b>$100 USD</b>; ఇతర దేశాలు <b>$100 USD</b> / <b>$150 USD</b>. రుసుములు భవిష్యత్తులో మారవచ్చు మరియు చెల్లింపుకు ముందు ఎల్లప్పుడూ ప్రదర్శించబడతాయి.</li>
          <li>చెల్లింపులు <b>Cashfree</b> ద్వారా ప్రాసెస్ చేయబడతాయి. మేము మీ పూర్తి కార్డ్ నంబర్, CVV లేదా UPI PIN నిల్వ చేయం.</li>
          <li>విజయవంతమైన చెల్లింపుపై పన్ను ఇన్‌వాయిస్ (GSTతో సహా) రూపొందించబడుతుంది.</li>
          <li>ప్లాట్‌ఫాం కమీషన్ (గరిష్టంగా 20%) {COMPANY_TE} వద్ద ఉంచుతుంది; మిగిలినది వైద్యుడికి చెల్లించబడుతుంది.</li>
        </ul>

        <H2>5. రద్దు &amp; రీఫండ్ విధానం</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>వైద్యుడు అప్పగించబడకముందు</b> — అభ్యర్థనపై పూర్తి రీఫండ్.</li>
          <li><b>అప్పగింత తర్వాత కానీ అభిప్రాయం అందజేయబడకముందు</b> — సమీక్ష ప్రారంభం కాకపోతే పూర్తి రీఫండ్, లేకపోతే 50% పాక్షిక రీఫండ్.</li>
          <li><b>అభిప్రాయం అందజేయబడిన తర్వాత</b> — రీఫండ్ లేదు.</li>
          <li><b>వైద్యుడు తిరస్కరించడం / 48 గంటలు (ప్రామాణిక) / 6 గంటలు (ఎమర్జెన్సీ) లోపల ప్రతిస్పందన లేదు</b> — స్వయంచాలక పూర్తి రీఫండ్.</li>
          <li>రీఫండ్‌లు 5–7 పని దినాల్లో అసలు చెల్లింపు మూలానికి క్రెడిట్ చేయబడతాయి.</li>
        </ul>

        <H2>6. వైద్య-రోగి సంబంధం &amp; అభిప్రాయ పరిధి</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>బుక్ చేసిన ఒకే కేసు కోసం అప్పగించబడిన వైద్యుడు మరియు రోగి మధ్య పరిమిత టెలీ-కన్సల్టేషన్ సంబంధం ఏర్పడుతుంది.</li>
          <li>అభిప్రాయం మీరు అందించిన సమాచారం మరియు స్కాన్‌ల ఆధారంగా మాత్రమే.</li>
          <li>అభిప్రాయం ప్రత్యక్ష పరీక్షకు <b>ప్రత్యామ్నాయం కాదు</b>.</li>
          <li>ప్లాట్‌ఫాం షెడ్యూల్ X మందులు లేదా మాదకద్రవ్యాలను <b>సూచించదు</b>.</li>
        </ul>

        <H2>7. ఆమోదయోగ్యమైన వినియోగం</H2>
        <p>మీరు ఈ క్రింది వాటిని చేయకూడదని అంగీకరిస్తారు:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>తప్పుడు, కల్పిత లేదా అధికారం లేకుండా మరొకరి వైద్య రికార్డులను అప్‌లోడ్ చేయడం.</li>
          <li>ఎవరినైనా వేధించడం, దుర్వినియోగం, పరువు నష్టం, బెదిరించడం లేదా వేషం వేయడం.</li>
          <li>ప్లాట్‌ఫాం భద్రతను రివర్స్-ఇంజనీర్, స్క్రేప్ లేదా జోక్యం చేసుకోవడం.</li>
          <li>చట్టవిరుద్ధ, మోసపూరిత లేదా హానికరమైన ప్రయోజనం కోసం ఉపయోగించడం.</li>
          <li>వైద్యుడి వ్రాతపూర్వక అంగీకారం లేకుండా Jitsi కాల్‌లు లేదా అభిప్రాయాలను రికార్డ్ / పంచుకోవడం.</li>
        </ul>

        <H2>8. వైద్య బాధ్యతలు</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>చెల్లుబాటు అయ్యే MCI / NMC రిజిస్ట్రేషన్ నిర్వహించడం.</li>
          <li>SLA (48 గంటలు / 6 గంటలు) లోపల అప్పగించిన కేసులకు ప్రతిస్పందించడం.</li>
          <li>MCI ఎథిక్స్ కోడ్ మరియు DPDP చట్టం ప్రకారం రోగి గోప్యతను కాపాడటం.</li>
          <li>అభిప్రాయం అందజేసిన తర్వాత కనీసం 12 నెలల పాటు ప్లాట్‌ఫాం వెలుపల రోగులను ఆహ్వానించకపోవడం.</li>
          <li>స్వంత పేరు మరియు రిజిస్ట్రేషన్ నంబర్‌పై మాత్రమే నివేదికలు జారీ చేయడం; ఘోస్ట్-రైటింగ్ ఖచ్చితంగా నిషేధం.</li>
        </ul>

        <H2>9. మేధో సంపత్తి</H2>
        <p>ప్లాట్‌ఫాంలోని అన్ని కంటెంట్ {COMPANY_TE}కి స్వంతం / లైసెన్స్ పొందినది మరియు భారత మరియు అంతర్జాతీయ కాపీరైట్ మరియు ట్రేడ్‌మార్క్ చట్టం ద్వారా రక్షించబడింది. మీరు అప్‌లోడ్ చేసిన వైద్య రికార్డుల యాజమాన్యాన్ని మీరు ఉంచుకుంటారు మరియు సేవను అందించడానికి మాత్రమే వాటిని నిల్వ చేయడానికి, ప్రదర్శించడానికి మరియు ప్రసారం చేయడానికి మాకు పరిమిత, నాన్-ఎక్స్‌క్లూసివ్, రాయల్టీ-రహిత లైసెన్స్ మంజూరు చేస్తారు.</p>

        <H2>10. డిస్క్లెయిమర్‌లు &amp; బాధ్యత పరిమితి</H2>
        <p>ప్లాట్‌ఫాం &quot;ఉన్నది ఉన్నట్టుగా&quot; మరియు &quot;అందుబాటులో ఉన్నట్టుగా&quot; అందించబడుతుంది. {COMPANY_TE} ఏ పరోక్ష, యాదృచ్ఛిక, ప్రత్యేక, పర్యవసాన లేదా శిక్షా నష్టాలకు, అభిప్రాయం నుండి ఉత్పన్నమైన వైద్య ఫలితాలకు, లేదా మూడవ పక్ష సేవా ఆలస్యాలకు బాధ్యత వహించదు. మా మొత్తం బాధ్యత గత 90 రోజుల్లో చెల్లించిన రుసుము మించకూడదు, గరిష్ట పరిమితి <b>₹10,000</b>.</p>

        <H2>11. నష్టపరిహారం</H2>
        <p>ఈ నిబంధనలు లేదా వర్తించే చట్టాన్ని ఉల్లంఘించడం వల్ల వచ్చే ఏదైనా క్లెయిమ్, బాధ్యత, నష్టం, వ్యయం లేదా ఖర్చు నుండి {COMPANY_TE}, దాని వైద్యులు, అధికారులు, డైరెక్టర్లు, ఉద్యోగులు మరియు ఏజెంట్లను మీరు నష్టపరిహారం చెల్లించి రక్షిస్తారు.</p>

        <H2>12. రద్దు &amp; సస్పెన్షన్</H2>
        <p>మీరు ఈ నిబంధనలను ఉల్లంఘించారని మేము సహేతుకంగా నమ్మితే, ఎప్పుడైనా మీ ఖాతాను సస్పెండ్ లేదా రద్దు చేయవచ్చు. <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700">{SUPPORT_EMAIL}</a>కు ఇమెయిల్ చేయడం ద్వారా మీరు ఎప్పుడైనా మీ ఖాతాను రద్దు చేయవచ్చు.</p>

        <H2>13. పాలక చట్టం &amp; అధికార పరిధి</H2>
        <p>ఈ నిబంధనలు భారతదేశ చట్టాల ద్వారా నిర్వహించబడతాయి. అన్ని వివాదాలు <b>{CITY_TE}, {STATE_TE} వద్ద ఉన్న న్యాయస్థానాల ప్రత్యేక అధికార పరిధికి</b> లోబడి ఉంటాయి. కోర్టు ఎంపికకు ముందు పార్టీలు మధ్యవర్తిత్వ చట్టం 2023 కింద 60 రోజుల్లోపు సత్యసంధంగా మధ్యవర్తిత్వం చేసుకోవడానికి అంగీకరిస్తారు.</p>

        <H2>14. ఫిర్యాదు అధికారి</H2>
        <p>IT (ఇంటర్మీడియరీ మార్గదర్శకాలు) నియమాలు 2021 నియమం 3(2) మరియు DPDP చట్టం 2023 సెక్షన్ 10 ప్రకారం, ఫిర్యాదులను ఇలా పంపవచ్చు:<br /><b>ఫిర్యాదు అధికారి, {COMPANY_TE}</b>, {CITY_TE}, {STATE_TE}, {COUNTRY_TE}<br />ఇమెయిల్: <a href={`mailto:${GRIEVANCE_EMAIL}`} className="text-blue-700">{GRIEVANCE_EMAIL}</a><br />ప్రతిస్పందన సమయం: 48 గంటలు (అంగీకారం), 30 రోజులు (పరిష్కారం).</p>

        <H2>15. మార్పులు</H2>
        <p>ఈ నిబంధనలను ఎప్పుడైనా సవరించవచ్చు. ముఖ్యమైన మార్పులు అమలులోకి రావడానికి కనీసం 14 రోజుల ముందు సమాచారం ఇవ్వబడుతుంది. కొనసాగింపు వినియోగం అంగీకారాన్ని సూచిస్తుంది.</p>

        <H2>16. ఇతరములు</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>విభజనీయత:</b> ఏదైనా నిబంధన అమలు చేయలేకపోతే, మిగిలినవి పూర్తి శక్తిలో ఉంటాయి.</li>
          <li><b>మాఫీ:</b> ఏదైనా హక్కును అమలు చేయడంలో వైఫల్యం ఆ హక్కును వదిలివేయడం కాదు.</li>
          <li><b>బదిలీ:</b> మీరు ఈ నిబంధనలను బదిలీ చేయలేరు; మేము వారసునికి బదిలీ చేయవచ్చు.</li>
          <li><b>మొత్తం ఒప్పందం:</b> ఈ నిబంధనలు, గోప్యతా విధానం మరియు వైద్య డిస్క్లెయిమర్ మొత్తం ఒప్పందాన్ని ఏర్పరుస్తాయి.</li>
          <li><b>ఫోర్స్ మజ్యుర్:</b> సహేతుకమైన నియంత్రణకు మించిన సంఘటనల వల్ల కలిగే ఆలస్యాలకు ఏ పక్షం బాధ్యత వహించదు.</li>
        </ul>
      </div>
    </>
  );
}

export function TermsOfService() {
  const { lang } = useI18n();
  return <Layout testId="terms-page">{lang === "te" ? <TermsTE /> : <TermsEN />}</Layout>;
}

// ---------- MEDICAL DISCLAIMER ----------
function DisclaimerEN() {
  return (
    <>
      <H1 icon={<AlertTriangle className="w-6 h-6" />} title="Medical Disclaimer" color="amber" />
      <p className="text-sm text-slate-500 mb-6">Last updated: {LAST_UPDATED_EN}</p>
      <div className="rounded-xl border-2 border-red-200 bg-red-50 p-5 mb-6" data-testid="emergency-banner">
        <div className="text-red-900 font-semibold text-base">This is NOT an emergency service.</div>
        <div className="text-red-800 text-sm mt-1">If you or someone near you is experiencing a stroke, seizure, sudden severe headache, loss of consciousness or any medical emergency, please call <b>102 (Ambulance)</b> or <b>108</b> immediately, or go to the nearest hospital.</div>
      </div>
      <div className="prose prose-slate max-w-none text-sm text-slate-700 space-y-3 leading-relaxed">
        <p>{COMPANY} provides asynchronous <b>second-opinion consultations</b> from qualified neurologists and neurosurgeons registered with the Medical Council of India / National Medical Commission. Our service is intended to complement — not replace — a direct, in-person examination, and is delivered strictly per the <b>Telemedicine Practice Guidelines, 2020</b>.</p>
        <H2>What our service does</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>Provides a written expert opinion based on the symptoms and scans you upload.</li>
          <li>Connects you with MCI / NMC-verified specialists.</li>
          <li>Stores your medical data securely and privately.</li>
        </ul>
        <H2>What our service does NOT do</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>Does not perform physical examinations.</li>
          <li>Does not prescribe controlled substances, narcotics, or Schedule X drugs.</li>
          <li>Does not replace an in-person hospital visit when physical intervention is required.</li>
          <li>Does not handle time-critical emergencies.</li>
        </ul>
        <H2>Specialist credentials</H2>
        <p>Every doctor on {COMPANY} is required to submit:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>A valid MCI / NMC registration number, verified against the official registry.</li>
          <li>A scanned medical licence reviewed by our admin team before account activation.</li>
        </ul>
        <H2>Data accuracy</H2>
        <p>The quality of the second opinion depends entirely on the accuracy of what you share. Please upload the most recent and clearest scans, and describe your symptoms honestly and completely.</p>
        <H2>Refund policy</H2>
        <p>If the assigned doctor declines a case or does not deliver an opinion within a reasonable time, the full consultation fee is refunded. Platform fees are non-refundable once a written opinion is delivered. See the full <Link to="/terms" className="text-blue-700">Refund Policy in our Terms of Service</Link>.</p>
        <H2>Limitation of service</H2>
        <p>Nothing on this Platform should be construed as a guarantee of cure or specific medical outcome. Individual responses to medical interventions vary, and no medical opinion can eliminate uncertainty.</p>
        <H2>Contact</H2>
        <p>Medical queries: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700">{SUPPORT_EMAIL}</a><br />Grievance Officer: <a href={`mailto:${GRIEVANCE_EMAIL}`} className="text-blue-700">{GRIEVANCE_EMAIL}</a></p>
      </div>
    </>
  );
}

function DisclaimerTE() {
  return (
    <>
      <H1 icon={<AlertTriangle className="w-6 h-6" />} title="వైద్య డిస్క్లెయిమర్" color="amber" />
      <p className="text-sm text-slate-500 mb-6">చివరిగా నవీకరించబడింది: {LAST_UPDATED_TE}</p>
      <div className="rounded-xl border-2 border-red-200 bg-red-50 p-5 mb-6" data-testid="emergency-banner">
        <div className="text-red-900 font-semibold text-base">ఇది ఎమర్జెన్సీ సేవ కాదు.</div>
        <div className="text-red-800 text-sm mt-1">మీరు లేదా మీ సమీపంలో ఎవరైనా స్ట్రోక్, మూర్ఛ, ఆకస్మిక తీవ్రమైన తలనొప్పి, స్పృహ కోల్పోవడం లేదా ఏదైనా వైద్య అత్యవసర పరిస్థితిని ఎదుర్కొంటే, వెంటనే <b>102 (అంబులెన్స్)</b> లేదా <b>108</b>కు కాల్ చేయండి, లేదా సమీప ఆసుపత్రికి వెళ్లండి.</div>
      </div>
      <div className="prose prose-slate max-w-none text-sm text-slate-700 space-y-3 leading-relaxed">
        <p>{COMPANY_TE} MCI / NMC రిజిస్టర్డ్ అర్హత గల న్యూరాలజిస్టులు మరియు న్యూరోసర్జన్ల నుండి అసింక్రోనస్ <b>సెకండ్ ఒపీనియన్ కన్సల్టేషన్‌లను</b> అందిస్తుంది. మా సేవ ప్రత్యక్ష వ్యక్తిగత పరీక్షను భర్తీ చేయదు — పూర్తి చేస్తుంది — మరియు ఖచ్చితంగా <b>టెలీమెడిసిన్ ప్రాక్టీస్ మార్గదర్శకాలు 2020</b> ప్రకారం అందించబడుతుంది.</p>
        <H2>మా సేవ చేసేది</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>మీరు అప్‌లోడ్ చేసిన లక్షణాలు మరియు స్కాన్‌ల ఆధారంగా వ్రాతపూర్వక నిపుణుల అభిప్రాయం అందిస్తుంది.</li>
          <li>MCI / NMC-ధృవీకరించబడిన నిపుణులతో మిమ్మల్ని కలుపుతుంది.</li>
          <li>మీ వైద్య డేటాను సురక్షితంగా మరియు ప్రైవేట్‌గా నిల్వ చేస్తుంది.</li>
        </ul>
        <H2>మా సేవ చేయనిది</H2>
        <ul className="list-disc pl-5 space-y-1">
          <li>భౌతిక పరీక్షలు నిర్వహించదు.</li>
          <li>నియంత్రిత పదార్థాలు, మాదకద్రవ్యాలు లేదా షెడ్యూల్ X మందులను సూచించదు.</li>
          <li>భౌతిక జోక్యం అవసరమైనప్పుడు ప్రత్యక్ష ఆసుపత్రి సందర్శనను భర్తీ చేయదు.</li>
          <li>సమయ-క్లిష్ట అత్యవసర పరిస్థితులను నిర్వహించదు.</li>
        </ul>
        <H2>నిపుణుల అర్హతలు</H2>
        <p>{COMPANY_TE}లో ప్రతి వైద్యుడు సమర్పించాలి:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>అధికారిక రిజిస్ట్రీకి వ్యతిరేకంగా ధృవీకరించబడిన చెల్లుబాటు అయ్యే MCI / NMC రిజిస్ట్రేషన్ నంబర్.</li>
          <li>ఖాతా సక్రియం చేయడానికి ముందు మా అడ్మిన్ బృందం సమీక్షించిన స్కాన్ చేసిన వైద్య లైసెన్స్.</li>
        </ul>
        <H2>డేటా ఖచ్చితత్వం</H2>
        <p>సెకండ్ ఒపీనియన్ నాణ్యత మీరు పంచుకునే దాని ఖచ్చితత్వంపై పూర్తిగా ఆధారపడి ఉంటుంది. దయచేసి అత్యంత ఇటీవలి మరియు స్పష్టమైన స్కాన్‌లను అప్‌లోడ్ చేయండి మరియు మీ లక్షణాలను నిజాయితీగా మరియు పూర్తిగా వివరించండి.</p>
        <H2>రీఫండ్ విధానం</H2>
        <p>అప్పగించిన వైద్యుడు కేసును తిరస్కరిస్తే లేదా సహేతుకమైన సమయంలో అభిప్రాయం అందజేయకపోతే, పూర్తి కన్సల్టేషన్ రుసుము తిరిగి చెల్లించబడుతుంది. వ్రాతపూర్వక అభిప్రాయం అందజేయబడిన తర్వాత ప్లాట్‌ఫాం రుసుములు తిరిగి ఇవ్వబడవు. <Link to="/terms" className="text-blue-700">సేవా నిబంధనలలోని పూర్తి రీఫండ్ విధానం చూడండి</Link>.</p>
        <H2>సేవ పరిమితి</H2>
        <p>ఈ ప్లాట్‌ఫాంలోని ఏదీ నయం లేదా నిర్దిష్ట వైద్య ఫలితానికి హామీగా భావించకూడదు. వైద్య జోక్యాలకు వ్యక్తిగత ప్రతిస్పందనలు మారుతాయి మరియు ఏ వైద్య అభిప్రాయం అనిశ్చితిని తొలగించదు.</p>
        <H2>సంప్రదింపు</H2>
        <p>వైద్య ప్రశ్నలు: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-700">{SUPPORT_EMAIL}</a><br />ఫిర్యాదు అధికారి: <a href={`mailto:${GRIEVANCE_EMAIL}`} className="text-blue-700">{GRIEVANCE_EMAIL}</a></p>
      </div>
    </>
  );
}

export function MedicalDisclaimer() {
  const { lang } = useI18n();
  return <Layout testId="disclaimer-page">{lang === "te" ? <DisclaimerTE /> : <DisclaimerEN />}</Layout>;
}
